import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import ShopMediaUploadSection from './ShopMediaUploadSection.jsx';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Modal to view and manage shop media (logo + shop images). Fetches arrays on open and refetches on update.
 */
const ShopViewMediaModal = ({ shop, onClose, onMediaUpdate }) => {
    const shopId = shop?._id;
    const [shopLogoUrls, setShopLogoUrls] = useState(shop?.shopLogo || []);
    const [shopImageUrls, setShopImageUrls] = useState(shop?.shopImage || []);
    const [shopPriceImageUrls, setShopPriceImageUrls] = useState(shop?.shopPriceImage || []);

    const fetchMedia = () => {
        if (!shopId) return;
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' };
        axios
            .get(`${API_BASE}/api/shops/${shopId}/media`, { headers })
            .then((res) => {
                if (res.data?.success && res.data?.data) {
                    const d = res.data.data;
                    setShopLogoUrls(d.shopLogo || []);
                    setShopImageUrls(d.shopImage || []);
                    setShopPriceImageUrls(d.shopPriceImage || []);
                }
            })
            .catch(() => {});
    };

    useEffect(() => {
        fetchMedia();
    }, [shopId]);

    const handleLogoChange = (urls) => {
        setShopLogoUrls(urls);
        onMediaUpdate?.();
    };

    const handleImageChange = (urls) => {
        setShopImageUrls(urls);
        onMediaUpdate?.();
    };

    const handlePriceImageChange = (urls) => {
        setShopPriceImageUrls(urls);
        onMediaUpdate?.();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex-shrink-0 border-b px-4 py-3 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Media – <span className="text-blue-700">{shop?.shopName || 'Shop'}</span>
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 text-gray-500 hover:text-gray-700"
                        aria-label="Close"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    <ShopMediaUploadSection
                        label="Shop Logo"
                        mediaType="shopLogo"
                        urls={shopLogoUrls}
                        onUrlsChange={handleLogoChange}
                        shopId={shopId}
                        multiple={false}
                    />
                    <ShopMediaUploadSection
                        label="Shop Images"
                        mediaType="shopImage"
                        urls={shopImageUrls}
                        onUrlsChange={handleImageChange}
                        shopId={shopId}
                        multiple={true}
                    />
                    <ShopMediaUploadSection
                        label="Shop Price Images"
                        mediaType="shopPriceImage"
                        urls={shopPriceImageUrls}
                        onUrlsChange={handlePriceImageChange}
                        shopId={shopId}
                        multiple={true}
                    />
                </div>
            </div>
        </div>
    );
};

ShopViewMediaModal.propTypes = {
    shop: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        shopName: PropTypes.string,
        shopLogo: PropTypes.arrayOf(PropTypes.string),
        shopImage: PropTypes.arrayOf(PropTypes.string),
        shopPriceImage: PropTypes.arrayOf(PropTypes.string),
    }),
    onClose: PropTypes.func.isRequired,
    onMediaUpdate: PropTypes.func,
};

export default ShopViewMediaModal;
